import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  CalendarDays,
  Moon,
  Sun,
  Plus,
  Trash2,
  Save,
  Clock,
  Power,
  Globe,
  Users,
  MapPin,
  Coins,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const DAYS = [
  { idx: 0, label: "Mon", full: "Monday" },
  { idx: 1, label: "Tue", full: "Tuesday" },
  { idx: 2, label: "Wed", full: "Wednesday" },
  { idx: 3, label: "Thu", full: "Thursday" },
  { idx: 4, label: "Fri", full: "Friday" },
  { idx: 5, label: "Sat", full: "Saturday" },
  { idx: 6, label: "Sun", full: "Sunday" },
];

const COMMON_TZS = [
  "Europe/Warsaw",
  "Europe/London",
  "Europe/Berlin",
  "America/New_York",
  "America/Los_Angeles",
  "Asia/Tokyo",
  "Asia/Singapore",
  "UTC",
];

const minToHHMM = (m) => {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};
const HHMMToMin = (s) => {
  if (!s || typeof s !== "string") return 0;
  const [h, m] = s.split(":").map((x) => parseInt(x, 10) || 0);
  return Math.max(0, Math.min(1440, h * 60 + m));
};

const todayISOLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Format an ISO UTC datetime into a short label in a given timezone.
const formatLocalShort = (iso, tz) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: tz,
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch (e) {
    return iso;
  }
};

const formatMesosShort = (n) => {
  const num = Number(n) || 0;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(num >= 10_000_000 ? 0 : 1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(0)}k`;
  return String(num);
};

// ============================================================================
// Weekly grid editor (7 cols x 48 half-hour rows)
// ============================================================================
// Each cell = 30 minute block. Click to toggle; drag to paint.
const SLOT_MINUTES = 30;
const SLOTS_PER_DAY = 1440 / SLOT_MINUTES; // 48

const buildDayMask = (slots, day) => {
  // returns array of size SLOTS_PER_DAY booleans
  const mask = new Array(SLOTS_PER_DAY).fill(false);
  (slots || []).forEach((s) => {
    if (s.day_of_week !== day) return;
    const startIdx = Math.floor((s.start_min || 0) / SLOT_MINUTES);
    const endIdx = Math.ceil((s.end_min || 0) / SLOT_MINUTES);
    for (let i = startIdx; i < endIdx; i++) {
      if (i >= 0 && i < SLOTS_PER_DAY) mask[i] = true;
    }
  });
  return mask;
};

const maskToSlots = (mask, day) => {
  // collapse consecutive trues into {day_of_week, start_min, end_min}
  const out = [];
  let i = 0;
  while (i < SLOTS_PER_DAY) {
    if (mask[i]) {
      const start = i * SLOT_MINUTES;
      while (i < SLOTS_PER_DAY && mask[i]) i++;
      const end = i * SLOT_MINUTES;
      out.push({ day_of_week: day, start_min: start, end_min: end });
    } else {
      i++;
    }
  }
  return out;
};

const WeeklyGridEditor = ({ availableSlots, sleepSlots, onChange, mode, appointments, tz }) => {
  // mode is informational — both grids share state but rendered separately
  const [drag, setDrag] = useState(null); // {dayIdx, addMode}

  // Build masks per day
  const slots = mode === "sleep" ? sleepSlots : availableSlots;
  const masks = useMemo(() => {
    return DAYS.map((d) => buildDayMask(slots, d.idx));
  }, [slots]);

  // Build appointment markers per (day-of-week, slotIdx) — counts customer
  // appointments whose admin-local time-of-day overlaps each 30-min cell. Only
  // applies to the "available" grid so admins can see at a glance which
  // recurring time-of-day blocks have booked customer meetings.
  const apptMarkers = useMemo(() => {
    if (mode !== "available") return null;
    const grid = Array.from({ length: 7 }, () => new Array(SLOTS_PER_DAY).fill(0));
    const apptDetails = Array.from({ length: 7 }, () => new Array(SLOTS_PER_DAY).fill(null).map(() => []));
    (appointments || []).forEach((a) => {
      if (!a.proposed_meeting_at) return;
      try {
        const d = new Date(a.proposed_meeting_at);
        // Convert to admin TZ
        const parts = new Intl.DateTimeFormat("en-GB", {
          timeZone: tz,
          weekday: "short",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }).formatToParts(d);
        const wk = parts.find((p) => p.type === "weekday")?.value || "";
        const hh = parseInt(parts.find((p) => p.type === "hour")?.value || "0", 10);
        const mm = parseInt(parts.find((p) => p.type === "minute")?.value || "0", 10);
        const dayMap = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
        const dayIdx = dayMap[wk];
        if (dayIdx == null) return;
        const slotIdx = Math.floor((hh * 60 + mm) / SLOT_MINUTES);
        if (slotIdx >= 0 && slotIdx < SLOTS_PER_DAY) {
          grid[dayIdx][slotIdx] += 1;
          apptDetails[dayIdx][slotIdx].push(a);
        }
      } catch (e) {
        // ignore
      }
    });
    return { grid, apptDetails };
  }, [appointments, tz, mode]);

  const setCell = (dayIdx, slotIdx, val) => {
    const newMasks = masks.map((m) => [...m]);
    newMasks[dayIdx][slotIdx] = val;
    const newSlots = [];
    for (let d = 0; d < 7; d++) {
      newSlots.push(...maskToSlots(newMasks[d], d));
    }
    onChange(newSlots);
  };

  const onCellPointerDown = (dayIdx, slotIdx) => (e) => {
    e.preventDefault();
    const cur = masks[dayIdx][slotIdx];
    const addMode = !cur;
    setCell(dayIdx, slotIdx, addMode);
    setDrag({ dayIdx, addMode });
  };
  const onCellPointerEnter = (dayIdx, slotIdx) => () => {
    if (!drag) return;
    if (drag.dayIdx !== dayIdx) return;
    setCell(dayIdx, slotIdx, drag.addMode);
  };

  useEffect(() => {
    const up = () => setDrag(null);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, []);

  // Hour labels every 2 hours
  const hourLabels = [];
  for (let h = 0; h < 24; h++) {
    hourLabels.push(h);
  }

  const cellOn = mode === "sleep" ? "bg-indigo-600/70" : "bg-emerald-500/70";
  const cellOnHover = mode === "sleep" ? "hover:bg-indigo-500" : "hover:bg-emerald-400";

  return (
    <div className="overflow-x-auto" data-testid={`weekly-grid-${mode}`}>
      <table className="text-xs select-none" style={{ borderSpacing: 0 }}>
        <thead>
          <tr>
            <th className="w-12 sticky left-0 bg-[#1f1208] text-orange-200/60 text-[10px] pr-1">
              {mode === "sleep" ? "Sleep" : "Avail."}
            </th>
            {DAYS.map((d) => (
              <th
                key={d.idx}
                className="text-orange-200 px-2 py-1 font-bold text-[11px]"
              >
                {d.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {hourLabels.map((h) => (
            <tr key={h}>
              <td className="text-[10px] text-orange-200/60 font-mono pr-1 align-top h-[28px] sticky left-0 bg-[#1f1208]">
                {String(h).padStart(2, "0")}:00
              </td>
              {DAYS.map((d) => {
                const slot0 = h * 2; // half-hour 1
                const slot1 = h * 2 + 1; // half-hour 2
                const on0 = masks[d.idx][slot0];
                const on1 = masks[d.idx][slot1];
                const ap0 = apptMarkers?.grid?.[d.idx]?.[slot0] || 0;
                const ap1 = apptMarkers?.grid?.[d.idx]?.[slot1] || 0;
                const ap0Details = apptMarkers?.apptDetails?.[d.idx]?.[slot0] || [];
                const ap1Details = apptMarkers?.apptDetails?.[d.idx]?.[slot1] || [];
                const tipFor = (arr) =>
                  arr
                    .map((x) => `${x.ign || x.discord || "(guest)"} — ${formatLocalShort(x.proposed_meeting_at, tz)}`)
                    .join("\n");
                return (
                  <td
                    key={d.idx}
                    className="p-0 border border-[#5a3a14]/40"
                    style={{ width: "44px" }}
                  >
                    <div className="grid grid-cols-1 gap-px">
                      <div
                        className={`relative h-[14px] cursor-pointer transition ${
                          on0 ? `${cellOn} ${cellOnHover}` : "bg-[#1a0e05] hover:bg-[#3a2814]"
                        }`}
                        onPointerDown={onCellPointerDown(d.idx, slot0)}
                        onPointerEnter={onCellPointerEnter(d.idx, slot0)}
                        data-testid={`grid-cell-${mode}-${d.idx}-${slot0}`}
                        title={
                          ap0 > 0
                            ? `${d.full} ${minToHHMM(slot0 * SLOT_MINUTES)} — ${minToHHMM((slot0 + 1) * SLOT_MINUTES)}\n${ap0} customer appointment${ap0 !== 1 ? "s" : ""}:\n${tipFor(ap0Details)}`
                            : `${d.full} ${minToHHMM(slot0 * SLOT_MINUTES)} — ${minToHHMM((slot0 + 1) * SLOT_MINUTES)}`
                        }
                      >
                        {ap0 > 0 && (
                          <span className="absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center pointer-events-none">
                            <span className="bg-amber-400 text-amber-950 text-[8px] font-extrabold rounded-sm px-1 leading-tight border border-amber-700 shadow-sm">
                              {ap0}
                            </span>
                          </span>
                        )}
                      </div>
                      <div
                        className={`relative h-[14px] cursor-pointer transition ${
                          on1 ? `${cellOn} ${cellOnHover}` : "bg-[#1a0e05] hover:bg-[#3a2814]"
                        }`}
                        onPointerDown={onCellPointerDown(d.idx, slot1)}
                        onPointerEnter={onCellPointerEnter(d.idx, slot1)}
                        data-testid={`grid-cell-${mode}-${d.idx}-${slot1}`}
                        title={
                          ap1 > 0
                            ? `${d.full} ${minToHHMM(slot1 * SLOT_MINUTES)} — ${minToHHMM((slot1 + 1) * SLOT_MINUTES)}\n${ap1} customer appointment${ap1 !== 1 ? "s" : ""}:\n${tipFor(ap1Details)}`
                            : `${d.full} ${minToHHMM(slot1 * SLOT_MINUTES)} — ${minToHHMM((slot1 + 1) * SLOT_MINUTES)}`
                        }
                      >
                        {ap1 > 0 && (
                          <span className="absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center pointer-events-none">
                            <span className="bg-amber-400 text-amber-950 text-[8px] font-extrabold rounded-sm px-1 leading-tight border border-amber-700 shadow-sm">
                              {ap1}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-[10px] text-orange-200/60 mt-1 italic">
        Click or drag (vertically) to {mode === "sleep" ? "mark sleep hours" : "mark available hours"} — granularity 30 min.
      </div>
    </div>
  );
};

// ============================================================================
// Quick sleep presets
// ============================================================================
const SleepPresets = ({ onApply }) => {
  const presets = [
    { label: "00:00–08:00 every day", start: 0, end: 480, days: [0, 1, 2, 3, 4, 5, 6] },
    { label: "23:00–07:00 every day (overnight)", start: 23 * 60, end: 24 * 60, days: [0, 1, 2, 3, 4, 5, 6], extra: { start: 0, end: 7 * 60 } },
    { label: "01:00–09:00 Mon-Fri (weekday)", start: 60, end: 9 * 60, days: [0, 1, 2, 3, 4] },
    { label: "Clear all sleep hours", clear: true },
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {presets.map((p, idx) => (
        <Button
          key={idx}
          variant="outline"
          size="sm"
          onClick={() => onApply(p)}
          data-testid={`sleep-preset-${idx}`}
        >
          {p.label}
        </Button>
      ))}
    </div>
  );
};

// ============================================================================
// One-off slots editor
// ============================================================================
const OneOffEditor = ({ slots, onChange }) => {
  const [date, setDate] = useState(todayISOLocal());
  const [start, setStart] = useState("18:00");
  const [end, setEnd] = useState("22:00");
  const [type, setType] = useState("available");
  const [note, setNote] = useState("");

  const add = () => {
    const s = HHMMToMin(start);
    const e = HHMMToMin(end);
    if (e <= s) {
      toast.error("End must be after start");
      return;
    }
    onChange([
      ...(slots || []),
      {
        id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        date,
        start_min: s,
        end_min: e,
        type,
        note: note.trim().slice(0, 120),
      },
    ]);
    setNote("");
  };

  const remove = (id) => {
    onChange((slots || []).filter((s) => s.id !== id));
  };

  const sorted = useMemo(
    () =>
      [...(slots || [])].sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return (a.start_min || 0) - (b.start_min || 0);
      }),
    [slots]
  );

  return (
    <div className="space-y-2" data-testid="one-off-editor">
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
        <div className="col-span-2">
          <div className="text-[10px] uppercase font-bold text-orange-200/80">
            Date (your local)
          </div>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-9"
            data-testid="oneoff-date"
          />
        </div>
        <div>
          <div className="text-[10px] uppercase font-bold text-orange-200/80">
            Start
          </div>
          <Input
            type="time"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="h-9"
            data-testid="oneoff-start"
          />
        </div>
        <div>
          <div className="text-[10px] uppercase font-bold text-orange-200/80">
            End
          </div>
          <Input
            type="time"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="h-9"
            data-testid="oneoff-end"
          />
        </div>
        <div>
          <div className="text-[10px] uppercase font-bold text-orange-200/80">
            Type
          </div>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="h-9 px-2 rounded border border-input bg-background text-sm w-full"
            data-testid="oneoff-type"
          >
            <option value="available">Available</option>
            <option value="sleep">Sleep</option>
            <option value="unavailable">Unavailable</option>
          </select>
        </div>
        <div>
          <Button onClick={add} className="w-full h-9" data-testid="oneoff-add">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="col-span-1 sm:col-span-2">
          <Input
            placeholder="Optional note (e.g. 'Holidays', 'Doctor visit')"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="h-9"
            data-testid="oneoff-note"
          />
        </div>
      </div>

      {/* List */}
      <div className="rounded-lg border-2 border-[#5a3a14] bg-[#1f1208] overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#3a2814] text-orange-200">
            <tr>
              <th className="text-left px-2 py-1.5 font-bold">Date</th>
              <th className="text-left px-2 py-1.5 font-bold">Window (local)</th>
              <th className="text-left px-2 py-1.5 font-bold">Type</th>
              <th className="text-left px-2 py-1.5 font-bold">Note</th>
              <th className="text-right px-2 py-1.5 font-bold"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="text-center py-3 text-orange-200/60 italic"
                >
                  No one-off entries yet.
                </td>
              </tr>
            ) : (
              sorted.map((s) => (
                <tr
                  key={s.id}
                  className="border-t border-[#5a3a14]/40"
                  data-testid={`oneoff-row-${s.id}`}
                >
                  <td className="px-2 py-1.5 font-mono">{s.date}</td>
                  <td className="px-2 py-1.5 font-mono">
                    {minToHHMM(s.start_min)} — {minToHHMM(s.end_min)}
                  </td>
                  <td className="px-2 py-1.5">
                    <Badge
                      variant="outline"
                      className={
                        s.type === "available"
                          ? "border-emerald-500 text-emerald-300"
                          : s.type === "sleep"
                          ? "border-indigo-500 text-indigo-300"
                          : "border-red-500 text-red-300"
                      }
                    >
                      {s.type}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5 text-orange-200/80">
                    {s.note || "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(s.id)}
                      className="h-7 w-7 text-red-400 hover:text-red-300"
                      data-testid={`oneoff-del-${s.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// Customer appointments panel — shows upcoming booked meetings with IGN, time,
// total payout. Distinct amber color so they stand out from the recurring grid.
// ============================================================================
const AppointmentsPanel = ({ appointments, tz, onChanged }) => {
  if (!appointments || appointments.length === 0) {
    return (
      <div
        className="rounded-lg border-2 border-amber-600/40 bg-[#2a1f08] p-3"
        data-testid="appointments-panel-empty"
      >
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-amber-300" />
          <div className="font-extrabold uppercase tracking-wider text-amber-200">
            Customer appointments
          </div>
          <Badge className="ml-auto bg-amber-500/10 border border-amber-500/40 text-amber-300 text-[10px]">
            0 booked
          </Badge>
        </div>
        <p className="text-[11px] text-amber-100/60 mt-1.5 italic">
          No customer appointments in the next 14 days. When a customer picks a
          time on the OreStore page, their booking will appear here and on the
          available grid below in amber.
        </p>
      </div>
    );
  }

  // Group appointments by admin-local date for compact display.
  const byDate = new Map();
  appointments.forEach((a) => {
    if (!a.proposed_meeting_at) return;
    let dateKey = "";
    try {
      const d = new Date(a.proposed_meeting_at);
      dateKey = new Intl.DateTimeFormat("en-CA", {
        timeZone: tz,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(d);
    } catch (e) {
      dateKey = a.proposed_meeting_at.slice(0, 10);
    }
    if (!byDate.has(dateKey)) byDate.set(dateKey, []);
    byDate.get(dateKey).push(a);
  });
  const sortedKeys = [...byDate.keys()].sort();

  return (
    <div
      className="rounded-lg border-2 border-amber-600/60 bg-[#2a1f08] p-3 space-y-2"
      data-testid="appointments-panel"
      style={{
        backgroundImage:
          "linear-gradient(180deg, #3a2a10 0%, #2a1f08 100%)",
      }}
    >
      <div className="flex items-center gap-2">
        <Users className="w-4 h-4 text-amber-300" />
        <div className="font-extrabold uppercase tracking-wider text-amber-200">
          Customer appointments
        </div>
        <Badge className="ml-auto bg-amber-400 text-amber-950 border-2 border-amber-700 text-[10px] font-extrabold shadow-sm">
          {appointments.length} booked
        </Badge>
      </div>
      <p className="text-[11px] text-amber-100/70">
        Upcoming booked meetings made by customers via the OreStore page. Times
        shown in your timezone (<strong className="text-amber-200">{tz}</strong>).
        These are <strong className="text-amber-200">not</strong> recurring — they appear here
        and as <strong className="text-amber-200">amber badges</strong> on the
        weekly available grid for the matching time-of-day.
      </p>

      <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
        {sortedKeys.map((k) => (
          <div key={k}>
            <div className="text-[10px] uppercase font-extrabold text-amber-300/80 tracking-widest mb-1 sticky top-0 bg-[#2a1f08] py-0.5">
              {formatDateHeader(k)}
            </div>
            <div className="space-y-1.5">
              {byDate.get(k).map((a) => (
                <AppointmentRow key={a.offer_id} appt={a} tz={tz} onChanged={onChanged} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const formatDateHeader = (yyyymmdd) => {
  try {
    const [y, m, d] = yyyymmdd.split("-").map((x) => parseInt(x, 10));
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.toLocaleDateString(undefined, {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  } catch (e) {
    return yyyymmdd;
  }
};

const AppointmentRow = ({ appt, tz, onChanged }) => {
  const timeOnly = (() => {
    try {
      return new Date(appt.proposed_meeting_at).toLocaleTimeString("en-GB", {
        timeZone: tz,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    } catch (e) {
      return appt.proposed_meeting_at;
    }
  })();
  const utcTime = (() => {
    try {
      return new Date(appt.proposed_meeting_at).toISOString().slice(11, 16) + " UTC";
    } catch (e) {
      return "";
    }
  })();
  const statusColors = {
    pending: "bg-orange-500/30 text-orange-100 border-orange-500",
    scheduled: "bg-emerald-500/30 text-emerald-100 border-emerald-500",
  };

  const markCompleted = async () => {
    if (!window.confirm(`Mark appointment with ${appt.ign || appt.discord || "(guest)"} as completed?`)) return;
    try {
      await axios.put(
        `${API_URL}/api/admin/orestore/offers/${appt.offer_id}/status`,
        { status: "completed" },
        { headers: getAuthHeaders() }
      );
      toast.success("Marked completed");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  return (
    <div
      className="grid grid-cols-[68px_1fr_auto] sm:grid-cols-[68px_1fr_auto_auto] items-center gap-2 p-2 rounded border-2 border-amber-500/40 bg-[#1f1505] hover:border-amber-400 transition"
      data-testid={`appt-row-${appt.offer_id}`}
    >
      <div className="text-center">
        <div className="font-mono text-amber-200 font-extrabold text-base leading-tight">
          {timeOnly}
        </div>
        <div className="text-[9px] text-amber-200/50 font-mono">{utcTime}</div>
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-amber-100 font-extrabold text-sm truncate">
            {appt.ign || appt.discord || "(guest)"}
          </span>
          <Badge
            className={`text-[9px] uppercase font-extrabold px-1.5 py-0 border ${statusColors[appt.status] || statusColors.pending}`}
          >
            {appt.status}
          </Badge>
          {appt.discord && appt.ign && (
            <span className="text-[10px] text-amber-200/60 truncate">
              · Discord: {appt.discord}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-amber-200/70 mt-0.5">
          <span className="inline-flex items-center gap-0.5">
            <Coins className="w-3 h-3" /> {formatMesosShort(appt.total_payout)}
          </span>
          <span>·</span>
          <span>{appt.items_count} item{appt.items_count !== 1 ? "s" : ""}</span>
          <span>·</span>
          <span className="inline-flex items-center gap-0.5">
            <MapPin className="w-3 h-3" /> FM15 CH15
          </span>
        </div>
        {appt.notes && (
          <div className="text-[10px] text-amber-100/60 italic mt-0.5 truncate">
            📝 {appt.notes}
          </div>
        )}
      </div>
      <div className="text-[10px] text-amber-300/60 font-mono hidden sm:block">
        #{(appt.offer_id || "").slice(0, 6)}
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={markCompleted}
        className="h-7 text-[10px] border-emerald-500 text-emerald-300 hover:bg-emerald-500/20"
        data-testid={`appt-complete-${appt.offer_id}`}
        title="Mark this trade as completed"
      >
        Done
      </Button>
    </div>
  );
};

// ============================================================================
// Main schedule panel
// ============================================================================
export const AdminOreStoreSchedulePanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [state, setState] = useState(null);
  const [serverNow, setServerNow] = useState(null);
  const [now, setNow] = useState(new Date());
  const [appointments, setAppointments] = useState([]);

  // editable buffers
  const [tz, setTz] = useState("Europe/Warsaw");
  const [weeklyAvail, setWeeklyAvail] = useState([]);
  const [weeklySleep, setWeeklySleep] = useState([]);
  const [oneOff, setOneOff] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/admin/orestore/availability`,
        { headers: getAuthHeaders() }
      );
      const sched = r.data?.schedule || {};
      setSchedule(sched);
      setState(r.data?.state || {});
      setServerNow(r.data?.server_now_utc || null);
      setAppointments(r.data?.appointments || []);
      setTz(sched.timezone || "Europe/Warsaw");
      setWeeklyAvail(sched.weekly_available || []);
      setWeeklySleep(sched.weekly_sleep || []);
      setOneOff(sched.one_off || []);
    } catch (e) {
      toast.error("Failed to load schedule");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Update clock every 5s + refresh state every 30s
  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 5000);
    const refresh = setInterval(() => {
      axios
        .get(`${API_URL}/api/admin/orestore/availability`, {
          headers: getAuthHeaders(),
        })
        .then((r) => {
          setState(r.data?.state || {});
          setServerNow(r.data?.server_now_utc || null);
          setAppointments(r.data?.appointments || []);
        })
        .catch(() => {});
    }, 30000);
    return () => {
      clearInterval(tick);
      clearInterval(refresh);
    };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/orestore/availability`,
        {
          timezone: tz,
          weekly_available: weeklyAvail,
          weekly_sleep: weeklySleep,
          one_off: oneOff,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Schedule saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save schedule");
    } finally {
      setSaving(false);
    }
  };

  const applySleepPreset = (p) => {
    if (p.clear) {
      setWeeklySleep([]);
      return;
    }
    const days = p.days || [];
    const out = [...weeklySleep];
    days.forEach((d) => {
      out.push({ day_of_week: d, start_min: p.start, end_min: p.end });
      if (p.extra) {
        out.push({ day_of_week: d, start_min: p.extra.start, end_min: p.extra.end });
      }
    });
    // Merge same-day slots into mask
    const merged = [];
    for (let d = 0; d < 7; d++) {
      const mask = buildDayMask(out, d);
      maskToSlots(mask, d).forEach((s) => merged.push(s));
    }
    setWeeklySleep(merged);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-orange-200/70">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const hbAgo = state?.seconds_since_heartbeat;

  // Server time display (in admin TZ)
  let serverTimeAdmin = "";
  let serverTimeUTC = "";
  if (serverNow) {
    try {
      const dt = new Date(serverNow);
      serverTimeAdmin = dt.toLocaleString("en-GB", {
        timeZone: tz,
        hour12: false,
      });
      serverTimeUTC = dt.toISOString().replace("T", " ").slice(0, 19) + " UTC";
    } catch (e) {
      serverTimeUTC = serverNow;
    }
  }

  return (
    <div className="space-y-4" data-testid="admin-schedule-panel">
      {/* Status header */}
      <div
        className="rounded-lg border-2 border-[#5a3a14] p-3 grid sm:grid-cols-3 gap-3"
        style={{
          backgroundImage: "linear-gradient(180deg, #3a230f 0%, #2a180a 100%)",
        }}
      >
        <div data-testid="admin-status-card">
          <div className="text-[10px] uppercase font-bold text-orange-200/70 flex items-center gap-1">
            <Power className="w-3 h-3" /> Live status
          </div>
          {state?.is_online ? (
            <div className="text-emerald-300 font-extrabold text-lg flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
              Online
            </div>
          ) : state?.is_scheduled_now ? (
            <div className="text-amber-300 font-extrabold text-lg flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-400" />
              Scheduled but away
            </div>
          ) : state?.in_blocker ? (
            <div className="text-indigo-300 font-extrabold text-lg flex items-center gap-1.5">
              <Moon className="w-4 h-4" /> Sleep / Unavailable
            </div>
          ) : (
            <div className="text-orange-200/70 font-extrabold text-lg flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-500" />
              Offline
            </div>
          )}
          <div className="text-[10px] text-orange-200/60 mt-1">
            Heartbeat:{" "}
            {hbAgo == null
              ? "never"
              : hbAgo <= 90
              ? `${hbAgo}s ago (live)`
              : `${hbAgo}s ago (stale)`}
          </div>
        </div>
        <div data-testid="server-time-card">
          <div className="text-[10px] uppercase font-bold text-orange-200/70 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Server time
          </div>
          <div className="font-mono text-orange-100">
            {now.toLocaleTimeString("en-GB", { hour12: false })}
          </div>
          <div className="text-[10px] text-orange-200/70 mt-1 font-mono">
            UTC: {serverTimeUTC || "—"}
            <br />
            {tz}: {serverTimeAdmin || "—"}
          </div>
        </div>
        <div data-testid="timezone-card">
          <div className="text-[10px] uppercase font-bold text-orange-200/70 flex items-center gap-1">
            <Globe className="w-3 h-3" /> Your timezone
          </div>
          <select
            value={tz}
            onChange={(e) => setTz(e.target.value)}
            className="h-9 px-2 rounded border border-input bg-background text-sm w-full mt-0.5"
            data-testid="tz-select"
          >
            {COMMON_TZS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
            {!COMMON_TZS.includes(tz) && <option value={tz}>{tz}</option>}
          </select>
          <div className="text-[10px] text-orange-200/60 mt-1">
            All schedules below are entered in this timezone and translated to UTC for users.
          </div>
        </div>
      </div>

      {/* Customer Appointments */}
      <AppointmentsPanel appointments={appointments} tz={tz} onChanged={load} />

      {/* Sleep section */}
      <div className="rounded-lg border-2 border-indigo-700/40 bg-[#1f1230] p-3 space-y-2" data-testid="sleep-section">
        <div className="flex items-center gap-2">
          <Moon className="w-4 h-4 text-indigo-300" />
          <div className="font-extrabold uppercase tracking-wider text-indigo-200">
            Sleep hours (always unavailable, unless you go online manually)
          </div>
        </div>
        <p className="text-[11px] text-indigo-100/70">
          Sleep hours block the OreStore from showing you as online — even if a
          weekly available slot covers the same time. Useful so users never
          message you at 3 AM.
        </p>
        <SleepPresets onApply={applySleepPreset} />
        <WeeklyGridEditor
          availableSlots={weeklyAvail}
          sleepSlots={weeklySleep}
          onChange={setWeeklySleep}
          mode="sleep"
          appointments={appointments}
          tz={tz}
        />
      </div>

      {/* Available section */}
      <div className="rounded-lg border-2 border-emerald-700/40 bg-[#102d1d] p-3 space-y-2" data-testid="available-section">
        <div className="flex items-center gap-2">
          <Sun className="w-4 h-4 text-emerald-300" />
          <div className="font-extrabold uppercase tracking-wider text-emerald-200">
            Weekly available hours (recurring)
          </div>
          {appointments.length > 0 && (
            <Badge className="bg-amber-500/20 border-2 border-amber-500 text-amber-200 text-[10px] font-extrabold uppercase ml-auto">
              <Users className="w-3 h-3 mr-1" /> {appointments.length} customer appointment{appointments.length !== 1 ? "s" : ""}
            </Badge>
          )}
        </div>
        <p className="text-[11px] text-emerald-100/70">
          Time windows when you typically can be reached. Users will see these
          translated to their own timezone. Online indicator requires you to be
          inside an available slot AND actually viewing the admin panel.
          {appointments.length > 0 && (
            <> Amber badges on cells mark <strong className="text-amber-200">customer-booked appointments</strong> at that recurring time-of-day — hover for IGN &amp; time.</>
          )}
        </p>
        <WeeklyGridEditor
          availableSlots={weeklyAvail}
          sleepSlots={weeklySleep}
          onChange={setWeeklyAvail}
          mode="available"
          appointments={appointments}
          tz={tz}
        />
      </div>

      {/* One-off section */}
      <div className="rounded-lg border-2 border-[#5a3a14] bg-[#2a1a0e] p-3 space-y-2" data-testid="oneoff-section">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-amber-300" />
          <div className="font-extrabold uppercase tracking-wider text-amber-200">
            One-off entries (overrides for specific dates)
          </div>
        </div>
        <p className="text-[11px] text-orange-200/70">
          Mark a specific date+time as <span className="text-emerald-300">available</span>,{" "}
          <span className="text-indigo-300">sleep</span> or{" "}
          <span className="text-red-300">unavailable</span> (e.g. holidays, doctor visits).
        </p>
        <OneOffEditor slots={oneOff} onChange={setOneOff} />
      </div>

      <div className="flex items-center justify-end gap-2 sticky bottom-2 bg-[#2a1a0e]/95 backdrop-blur p-2 rounded-lg border border-[#5a3a14] z-10">
        <Button
          variant="outline"
          onClick={load}
          disabled={saving}
          data-testid="schedule-reset"
        >
          Reset
        </Button>
        <Button onClick={save} disabled={saving} data-testid="schedule-save">
          {saving ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-1" />
          )}
          Save schedule
        </Button>
      </div>
    </div>
  );
};

export default AdminOreStoreSchedulePanel;
